import { CalendarBlankIcon } from '@phosphor-icons/react/dist/ssr';

import { dateFormatter } from '@/utils/dateFormatter';

import type { GroupSubscriptionData } from '../groupSubscriptionTypes';
import { GroupCardHeader } from './GroupCardHeader';

type ScheduledGroupCardProps = {
  group: GroupSubscriptionData;
  translations?: Record<string, string>;
};

const DEFAULT_COPY = {
  title: 'Starts soon',
  body: 'This group subscription starts on {{date}}. You can invite members once it begins.',
  body_no_date: "This group subscription hasn't started yet. You can invite members once it begins.",
} as const;

/**
 * A future-dated off-platform group parks as `scheduled` — the backend blocks
 * seat invitations until `term_starts_at` arrives (see `CreateInvitation`). We
 * mirror that here: no seat table or invite actions, just the header and a
 * "starts on {date}" banner, so the admin can't attempt an invite the API would
 * reject.
 */
export function ScheduledGroupCard({ group, translations }: ScheduledGroupCardProps) {
  // No invoice yet, so total_amount_cents may be null — estimate from
  // per-seat × max_seats so the header still shows a Total line.
  const estimatedTotalCents = group.total_amount_cents ?? group.per_seat_amount_cents * group.max_seats;
  // Force UTC — term_starts_at is a calendar date stored as UTC midnight.
  const startsDate = group.term_starts_at
    ? dateFormatter({ timeZone: 'UTC' }).format(new Date(group.term_starts_at))
    : null;

  const title = translations?.['group_subscriptions.card.scheduled.title'] ?? DEFAULT_COPY.title;
  const bodyTemplate = startsDate
    ? (translations?.['group_subscriptions.card.scheduled.body'] ?? DEFAULT_COPY.body)
    : (translations?.['group_subscriptions.card.scheduled.body_no_date'] ?? DEFAULT_COPY.body_no_date);
  const body = startsDate ? bodyTemplate.replace('{{date}}', startsDate) : bodyTemplate;

  return (
    <div className="flex flex-col gap-4 rounded-md border border-gray-200 bg-white p-4">
      <GroupCardHeader
        group={group}
        hideToggle
        hideInviteMenu
        totalCents={estimatedTotalCents}
        translations={translations}
        rightSlot={null}
      />

      <div className="flex items-start gap-3 rounded-md border border-blue-200 bg-blue-50 p-4">
        <CalendarBlankIcon weight="fill" width={20} height={20} className="mt-0.5 shrink-0 text-blue-500" />
        <div className="flex flex-col gap-1">
          <p className="text-sm font-medium leading-5 text-blue-900">{title}</p>
          <p className="text-sm font-normal leading-5 text-blue-900">{body}</p>
        </div>
      </div>
    </div>
  );
}
